// Packages
import React, { useState } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled, { css } from "styled-components"
import { motion } from "framer-motion"

// Components
import Header from "@components/global/default-header"
import Slider from "@components/abcares/slider"
import VideoModal from "@components/global/video-modal"
import ExternalLink from "@components/global/button-external-link"
import SEO from "@components/global/seo"

// Images
import ABCaresLogoBlue from "@images/abcares/AB Cares Logo.svg"
import ABCaresCircleLogo from "@images/abcares/AB Cares Circle Mark.svg"
import BlueHeartIcon from "@images/abcares/blue_heart_icon.svg"
import RedHeartIcon from "@images/abcares/heart_icon.svg"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

// Resuable Styled Components

const SmallBlueTitle = styled.div`
  color: var(--pacific-blue);
  text-align: ${props => (props.left ? "left" : "center")};
  margin-bottom: 4px;
`

const LargeTitle = styled.h2`
  font-style: normal;
  font-weight: normal;

  margin-bottom: 20px;
`

const Wrapper = styled.div`
  max-width: 1248px;
  padding: 0 48px;
  margin: 0 auto;
  ${below.tablet`
    margin: 48px auto 72px;
  `};
  ${below.mobile`
  padding: 0 24px;
 
  `}
`
//  Styled Components for page layout

const Mission = styled.div`
  width: 100%;
  background: url("/abcares/mission_bg.svg"), var(--btn-light-blue);
  padding: 80px 0;
  ${below.tablet`
  padding:64px; 0;
  `}

  ${below.mobile`
  padding:48px 0;
  `}
`

const MissionBlurbContainer = styled.div`
  width: 564px;

  margin: 0 auto;
  ${below.tablet`
    width:79.05%;
    `}
  ${below.mobile`
    width:87.46%;
    `}
`

const MissionBlurbDesktop = styled.h2`
  font-family: "Source Sans Pro";
  font-size: 36px;
  font-weight: 400;
  line-height: 44px;
  text-align: center;
  ${below.tablet`
    display:none;
    `}

  span {
    position: relative;
    z-index: 0;
    display: inline-block;
    font-weight: 700 !important;
    :after {
      content: "";
      position: absolute;
      border-bottom: 16px solid #fff2ab;
      bottom: 0px;
      left: 0;
      width: 100%;
      height: 16px;
      z-index: -1;
    }
  }
`
const MissionBlurbTablet = styled.h2`
  font-family: "Source Sans Pro";

  display: block;
  font-size: 36px;
  font-weight: 400;
  line-height: 44px;
  text-align: center;
  @media (min-width: 951px) {
    display: none;
  }
  ${below.mobile`
    display:none;
    `}

  span {
    position: relative;
    z-index: 0;
    display: inline-block;
    font-weight: 700 !important;
    :after {
      content: "";
      position: absolute;
      border-bottom: 16px solid #fff2ab;
      bottom: 0px;
      left: 0;
      width: 100%;
      height: 16px;
      z-index: -1;
    }
  }
`

const MissionBlurbMobile = styled.h2`
  font-family: "Source Sans Pro";

  display: block;
  font-size: 28px;
  line-height: 36px;
  font-weight: 400;
  text-align: center;
  @media (min-width: 601px) {
    display: none;
  }
  span {
    position: relative;
    z-index: 0;
    display: inline-block;
    font-weight: 700 !important;
    :after {
      content: "";
      position: absolute;
      border-bottom: 16px solid #fff2ab;
      bottom: 0px;
      left: 0;
      width: 100%;
      height: 16px;
      z-index: -1;
    }
  }
`

const OurWhy = styled.div`
  background: var(--ice-grey);
  position: relative;
  padding: 80px 0;
  ${below.tablet`
  padding:64px 0;
  `}

  ${below.mobile`
  padding:48px 0;
  `}
  .our-why {
    display: flex;
    ${below.tablet`
    flex-direction:column;
    margin-top:0;
    margin-bottom:0;
    position:relative;
    `}
    ${below.mobile`
    margin-bottom:0;
    `}
  }

  .ab-cares-circle-logo {
    position: absolute;
    margin-left: -84px;
    bottom: -84px;
    left: 50%;
    ${below.tablet`
    display:none;
    `}
  }
`

const OurWhyTextContainer = styled.div`
  width: 50%;
  margin-right: 40px;
  ${below.tablet`
    width:100%;
    margin-right:0;
    `}

  .our-why-large-title {
    ${below.mobile`

    margin-bottom:32px;
    `}
  }
  p {
    margin-bottom: 120px;
    ${below.tablet`
    margin-bottom:280px;
    `}
    ${below.mobile`

    margin-bottom:176px;
    `}
  }
`

const OurWhyImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  justify-content: space-between;
  align-items: flex-end;
  ${below.tablet`
    flex-direction:row;
    width:100%;
    position:absolute;
    bottom:-178.21px;
    `}
  ${below.mobile`
    justify-content:center;
    width:100%;
    position:absolute;
    left:0;
    bottom:-165.25px;
    `}


  .ab-cares-logo-blue {
    margin-bottom: 120px;
    display: none;
    ${below.tablet`
    display:initial;
    width:101px;
    position:absolute;
    right:98px;
    top:80.77px;
    `}
    ${below.mobile`
      display:none;
    `}
  }
`

const BikeImageContainer = styled.div`
  max-width: 400px;
  width: 100%;
  margin-right: 36px;
  margin-bottom: 38.14px;
  box-shadow: 0px 2px 32px rgba(153, 153, 153, 0.2);
  transform: rotate(-5deg);
  ${below.tablet`
    margin-top:32px;
    margin-bottom:0;
    `}
  ${below.mobile`
    max-width:304px;
    margin-top:0;
    margin-right:0;
    margin-bottom:0;
    transform: rotate(-2deg);

    `}
`

const HowWeOperate = styled.div`
  max-width: 1248px;
  margin: 0 auto;
  display: flex;
  padding: 160px 48px 184px;

  ${below.tablet`
    flex-direction: column-reverse;
    padding:296px 48px 120px;
    `}
  ${below.mobile`
    padding:232px 24px 48px;
`}
`
const HWOListContainer = styled.div`
  width: 50%;
  margin-right: 76px;
  margin-top: 24px;
  ${below.tablet`
    width:100%;
    margin:0;
    `}
  .last {
    margin-bottom: 0;
  }
`

const HWOTitleContainer = styled.div`
  width: 50%;
  ${below.tablet`
  width:100%;
    `}
  ${below.mobile`
    width:100%;
    `}
  .how-we-operate-small-blue-title {
    text-align: left;
    padding-top: 0;
  }

  .how-we-operate-large-title {
    margin-bottom: 64px;
    ${below.mobile`
    margin-bottom:40px;
    `}
  }
`

const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  background: var(--ice-grey);
  padding: 24px 32px;
  margin-bottom: 24px;
  max-width: 512px;
  ${below.tablet`
    background: var(--ice-grey);
    padding: 24px 32px;
    margin:0 auto 24px;
    `}
  ${below.mobile`
    margin-right:0;
    margin-left:0;

    `}
  img {
    margin-right: 24px;
    margin-bottom: 0;
  }

  p {
    font-size: 22px;
    line-height: 28px;
    margin-bottom: 0;
    ${below.mobile`
    font-size:20px;
    line-height:32px;
    `}
  }
`

const CommunityCollaborators = styled.div`
  max-width: 1248px;
  margin: 0 auto;
`
const CCTitleContainer = styled.div`
  padding: 0 16.94% 5.55%;
  @media (min-width: 1440px) {
    padding: 0 244px 80px;
  }
  ${below.tablet`
    padding: 0 48px 64px;
    `}
  ${below.mobile`
  max-width:500px;
    padding: 48px 24px 40px;
    `}

  .CC-small-blue-title {
    padding-top: 0;
    ${below.tablet`
    text-align:left;
    `}
  }

  .CC-large-title {
    text-align: center;
    margin-bottom: 0;
    ${below.tablet`
    text-align:left;

    `}
  }
`

const TellUsAboutTheCauses = styled.div`
  background: var(--ice-grey);
`

const TUATCTextContainer = styled.div`
  padding: 112px 0 112px;
  width: 562px;
  margin: 0 auto;
  text-align: center;

  ${below.tablet`
    padding:88px 0 88px;
    `}

  ${below.mobile`
    padding:64px 23.5px 64px;
    width:100%;
    `}

  .TUATC-large-title {
    text-align: center;
    margin-bottom: 32px;
    ${below.tablet`
    margin-bottom:40px;
    `}
    ${below.mobile`

    text-align:left;
    margin-bottom:24px;
    
    `}
    img {
      width: 32px;
      margin-left: 20px;
      ${below.tablet`
      margin-left:15px;
      width:24px;
    `}
      ${below.mobile`
    margin-left:10px;
    `}
    }
  }

  p {
    margin-bottom: 48px;
    ${below.mobile`
    text-align:left;
    margin-bottom:40px;
    `}
  }

  a {
    max-width: 188px;
    margin: 0 auto;
    ${below.mobile`
    max-width:unset;
    `}
  }

  /* a {
    display: block;
    width: 199px;
    height: 56px;
    padding: 12px 24px;
    background: var(--pacific-blue);
    text-align: center;
    color: white;
    border-radius: 4px;
    text-decoration: none;
    margin: 0 auto;
    font-size: 24px;

    ${below.tablet`
    width:174px;
    height:48px;
    font-size:19px;
    line-height:24px;
    `}

    ${below.mobile`
    margin:0;
    `}
  }

  a:hover {
    background: #e7f2fe;
    border: 1px solid var(--pacific-blue);
    box-sizing: border-box;
    color: var(--pacific-blue);
    @media (min-width: 851px) {
      font-size: 23.5px;
    }
  } */
`

const ABCares = props => {
  const [videoToggled, setVideoToggled] = useState(false)
  return (
    <>
      <Header
        hero={true}
        customHero={props.data.hero.childImageSharp.gatsbyImageData}
        headline="Atlantic Bay Cares"
        mobileHeadline="AB Cares"
        caption="Volunteering is near and dear to our hearts and building homes isn't the only philanthropic activity we enjoy, explore AB Cares for more!"
      />
      <Mission>
        <SmallBlueTitle className="sans overline-lrg">
          the mission
        </SmallBlueTitle>
        <MissionBlurbContainer>
          <MissionBlurbDesktop>
            Centering the core idea of <span>doing</span>{" "}
            <span>good and giving back</span> in the communities we serve.
          </MissionBlurbDesktop>
          <MissionBlurbTablet>
            Centering the core idea of <span>doing good and giving back</span>{" "}
            in the communities we serve.
          </MissionBlurbTablet>
          <MissionBlurbMobile>
            Centering the core idea of <span>doing good and giving</span>{" "}
            <span>back</span> in the communities we serve.
          </MissionBlurbMobile>
        </MissionBlurbContainer>
      </Mission>
      <OurWhy>
        <Wrapper className="our-why">
          <OurWhyTextContainer>
            <SmallBlueTitle
              left
              className="our-why-small-title sans overline-lrg"
            >
              our why
            </SmallBlueTitle>
            <LargeTitle className="our-why-large-title">
              Families have always been at the core of what we do.
            </LargeTitle>
            <p>
              It's more than mortgages. Putting people first has always been our
              number one priority. Through{" "}
              <strong>employee-run community impact committees</strong>, our
              team members voice their ideas and passions for causes meaningful
              to them, our customers, and the communities we serve. Then, we get
              to work.
            </p>
          </OurWhyTextContainer>
          <OurWhyImageContainer>
            <BikeImageContainer>
              <GatsbyImage
                image={props.data.bikeFluid.childImageSharp.gatsbyImageData}
                className="bike-image"
                alt=""
              />
            </BikeImageContainer>

            <img
              className="ab-cares-logo-blue"
              src={ABCaresLogoBlue}
              css={`
                width: 127px;
              `}
              alt="AB Cares logo"
            />
          </OurWhyImageContainer>
        </Wrapper>
        <img
          src={ABCaresCircleLogo}
          className="ab-cares-circle-logo"
          alt="AB Cares circle logo"
        />
      </OurWhy>

      <HowWeOperate>
        <HWOListContainer>
          <ListItem>
            <img src={BlueHeartIcon} alt="Blue heart icon" />
            <p>
              Changing lives in local communities through employee volunteerism.
            </p>
          </ListItem>
          <ListItem>
            <img src={BlueHeartIcon} alt="Blue heart icon" />
            <p>Giving back with financial support.</p>
          </ListItem>
          <ListItem className="last">
            <img src={BlueHeartIcon} alt="Blue heart icon" />
            <p>
              Increasing awareness of causes meaningful to our employees and
              customers.
            </p>
          </ListItem>
        </HWOListContainer>
        <HWOTitleContainer>
          <SmallBlueTitle className="how-we-operate-small-blue-title sans overline-lrg">
            how we operate
          </SmallBlueTitle>
          <LargeTitle className="how-we-operate-large-title">
            Communities backed by company-wide support.
          </LargeTitle>
        </HWOTitleContainer>
      </HowWeOperate>

      <CommunityCollaborators>
        <CCTitleContainer>
          <SmallBlueTitle className="CC-small-blue-title sans overline-lrg">
            community collaborators
          </SmallBlueTitle>
          <LargeTitle className="CC-large-title">
            Making a difference one home loan at a time.
          </LargeTitle>
        </CCTitleContainer>
        <Slider videoToggled={videoToggled} setVideoToggled={setVideoToggled} />
      </CommunityCollaborators>

      <TellUsAboutTheCauses>
        <TUATCTextContainer>
          <LargeTitle className="TUATC-large-title">
            Tell us about the causes close to your heart.
            <img src={RedHeartIcon} alt="red heart icon" />
          </LargeTitle>
          <p>
            If you have a cause you’re passionate about and know it needs
            funding or a group of volunteers, let us know!
          </p>
          <ExternalLink
            buttonText="Make a request"
            linkTo="https://atlanticbay.wufoo.com/forms/ab-cares-donation-request-form/"
            className="TUATC-link"
          />
        </TUATCTextContainer>
      </TellUsAboutTheCauses>

      {videoToggled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <VideoModal toggleModal={setVideoToggled} videoID="ijnjmjczvm" />
        </motion.div>
      )}
    </>
  )
}

// Meta tags
export const Head = props => {
  const canonicalSlug = props.pageContext.canonicalSlug
  return (
    <SEO
      description={
        "It's more than mortgages. Putting people first has always been our number one priority. Through employee-run community impact committees, our team members voice their ideas and passions for causes meaningful to them, our customers, and the communities we serve."
      }
      ogImage={
        "https://images.ctfassets.net/n1no4gieqp7s/5SDikyR6bnvESO2Er0Gz6z/d163b15133976f20be4ef4348424fe62/og-abcares.jpg"
      }
      title={`AB Cares | Atlantic Bay Mortgage Group®`}
      canonicalSlug={canonicalSlug}
      slug={props.location.pathname.replace(/^\//, "").replace(/\/$/, "")}
    />
  )
}

export default ABCares

export const data = graphql`
  query {
    hero: file(relativePath: { regex: "/abcares/abcares-house-build.jpg/" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      }
    }
    bikeFluid: file(relativePath: { regex: "/abcares/polaroid_bike.jpg/" }) {
      childImageSharp {
        gatsbyImageData(width: 400, layout: CONSTRAINED)
      }
    }
  }
`
